import { useColorModeValue } from '@chakra-ui/react';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import {
  CSSProperties,
  MouseEvent,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getStringWidth } from '../../utils/get-string-width';
import { useChartScaleColors, useChartStructureColors } from '../status-tag/chart-colors';
import {
  BarChartAxisLabel,
  getBarChartAxisLabelLines,
  ROTATED_WRAP_LABEL_LENGTH_THRESHOLD,
} from './bar-chart-axis-label';
import {
  CHART_SEGMENT_TOOLTIP_OFFSET,
  ChartSegmentTooltipContent,
  useChartTooltipStyles,
} from './chart-segment-tooltip';

export interface BarChartSeries {
  id: string;
  label: string;
  color: string;
  valueLabelBackground?: string;
  valueLabelColor?: string;
}

export interface BarChartGroup {
  id: string;
  label: string;
  values: Record<string, number | undefined>;
}

export type BarChartBarClickEvent = {
  group: BarChartGroup;
  series: BarChartSeries;
  value: number;
};

export interface BarChartProps {
  groups: BarChartGroup[];
  series: BarChartSeries[];
  height?: CSSProperties['height'];
  minHeight?: CSSProperties['minHeight'];
  xAxisMode?: 'group' | 'series';
  yMax?: number;
  yTickValues?: number[];
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  groupPadding?: number;
  groupOuterPadding?: number;
  seriesPadding?: number;
  seriesOuterPadding?: number;
  maxBarWidth?: number | null;
  minGroupWidth?: number;
  barBorderRadius?: number;
  showZeroBaseline?: boolean;
  rotateSeriesLabels?: boolean;
  integerTicks?: boolean;
  valueLabelFormatter?: (value: number) => string;
  xAxisLabelColor?: string;
  yAxisLabelColor?: string;
  gridColor?: string;
  showBarTooltip?: boolean;
  formatBarTooltip?: (event: BarChartBarClickEvent) => ReactNode;
  onBarClick?: (event: BarChartBarClickEvent) => void;
}

const VALUE_LABEL_AREA_HEIGHT = 32;
const VALUE_LABEL_HEIGHT = 20;
const VALUE_LABEL_PADDING_X = 8;
const VALUE_LABEL_GAP = 8;
const GROUP_AXIS_HEIGHT = 24;
const SERIES_AXIS_HEIGHT = 40;
const ROTATED_SERIES_LABEL_ANCHOR_OFFSET = 20;
const ROTATED_SERIES_LABEL_BOTTOM_PADDING = 8;
const ROTATED_SERIES_LABEL_MIN_BOTTOM_MARGIN = 32;
const ROTATED_SERIES_LABEL_LEFT_PADDING = 4;
const Y_AXIS_LABEL_GAP = 12;
const AXIS_FONT_SIZE = 12;
const AXIS_FONT_WEIGHT = 500;
const VALUE_LABEL_FONT_SIZE = 12;
const VALUE_LABEL_FONT_WEIGHT = 500;
const DEFAULT_BAR_CHART_MIN_HEIGHT = 320;

export function BarChart({
  height = '100%',
  minHeight = DEFAULT_BAR_CHART_MIN_HEIGHT,
  minGroupWidth,
  barBorderRadius,
  showZeroBaseline,
  margin = {},
  rotateSeriesLabels = false,
  showBarTooltip,
  formatBarTooltip,
  onBarClick,
  ...props
}: BarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ width: containerWidth, height: measuredHeight, parentWidth }, setContainerSize] =
    useState({
      width: 0,
      height: 0,
      parentWidth: 0,
    });

  useLayoutEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    const updateSize = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;
      const nextParentWidth = element.parentElement?.clientWidth ?? width;
      setContainerSize({ width, height, parentWidth: nextParentWidth });
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    observer.observe(element);
    const parentElement = element.parentElement;
    if (parentElement) {
      observer.observe(parentElement);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const { left: marginLeft = 0, right: marginRight = 0 } = margin;
  const minContentWidth = minGroupWidth
    ? props.groups.length * minGroupWidth + marginLeft + marginRight
    : undefined;
  const availableWidth = Math.floor(parentWidth > 0 ? parentWidth : containerWidth);
  const expandsHorizontally =
    minContentWidth !== undefined && availableWidth > 0 && availableWidth < minContentWidth;
  const svgWidth = expandsHorizontally ? minContentWidth : availableWidth;

  const fillsParent = height === '100%';

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height,
        minHeight,
        overflow: expandsHorizontally ? undefined : 'hidden',
        ...(expandsHorizontally ? { minWidth: minContentWidth } : {}),
        ...(fillsParent ? { flex: '1 1 0', alignSelf: 'stretch' } : {}),
      }}
    >
      <BarChartSvg
        {...props}
        width={svgWidth}
        height={measuredHeight}
        fluidWidth={!expandsHorizontally}
        margin={margin}
        barBorderRadius={barBorderRadius}
        showZeroBaseline={showZeroBaseline}
        rotateSeriesLabels={rotateSeriesLabels}
        showBarTooltip={showBarTooltip}
        formatBarTooltip={formatBarTooltip}
        onBarClick={onBarClick}
      />
    </div>
  );
}

interface BarChartSvgProps extends Omit<BarChartProps, 'height' | 'minHeight'> {
  width: number;
  height: number;
  fluidWidth?: boolean;
}

function BarChartSvg({
  groups,
  series,
  xAxisMode = 'group',
  yMax,
  yTickValues,
  margin = {},
  rotateSeriesLabels = false,
  groupPadding = 0.36,
  groupOuterPadding,
  seriesPadding = 0.3,
  seriesOuterPadding = 0.1,
  maxBarWidth = 32,
  barBorderRadius = 4,
  showZeroBaseline = false,
  integerTicks = false,
  valueLabelFormatter = formatBarChartValue,
  xAxisLabelColor,
  yAxisLabelColor,
  gridColor,
  showBarTooltip,
  formatBarTooltip,
  onBarClick,
  width = 0,
  height = 0,
  fluidWidth = false,
}: BarChartSvgProps) {
  const structure = useChartStructureColors();
  const resolvedXAxisLabelColor = structure.axisLabel;
  const resolvedYAxisLabelColor = structure.axisLabel;
  const resolvedGridColor = useColorModeValue('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.14)');
  /* The on-fill fallback: the neutral scale's chart label, mode-resolved,
   * for series that do not pass their own valueLabelColor. */
  const { label: defaultValueLabelColor } = useChartScaleColors('neutral');
  const resolvedTooltipStyles = useChartTooltipStyles();
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<BarChartBarClickEvent>();
  const barInteractive = showBarTooltip || !!onBarClick;
  const {
    top: marginTop = 0,
    right: marginRight = 0,
    bottom: marginBottom = 0,
    left: marginLeft = 0,
  } = margin;

  const chartWidth = Math.max(0, width ?? 0);
  const chartHeight = Math.max(0, height ?? 0);
  const usesRotatedSeriesLabels = xAxisMode === 'series' && rotateSeriesLabels;
  const axisHeight = usesRotatedSeriesLabels
    ? 0
    : xAxisMode === 'series'
      ? SERIES_AXIS_HEIGHT
      : GROUP_AXIS_HEIGHT;

  const derivedYMax = getBarChartScaleMax(groups, series);
  const requestedYMax = toSafePositiveBarMax(yMax);
  const baseYMax = Math.max(derivedYMax, requestedYMax ?? 0);
  const tickValues = useMemo(
    () => getBarChartTickValues(yTickValues, baseYMax, integerTicks),
    [baseYMax, yTickValues, integerTicks],
  );
  const safeYMax = Math.max(baseYMax, tickValues[0] ?? 0, 1);

  const contentLeft = useMemo(
    () => resolveContentLeft(marginLeft, tickValues),
    [marginLeft, tickValues],
  );

  const plotLeft = useMemo(
    () =>
      resolveBarsPlotLeft({
        contentLeft,
        marginLeft,
        marginRight,
        chartWidth,
        usesRotatedSeriesLabels,
        series,
        groups,
        groupPadding,
        groupOuterPadding,
        seriesPadding,
        seriesOuterPadding,
        maxBarWidth,
      }),
    [
      contentLeft,
      marginLeft,
      marginRight,
      chartWidth,
      usesRotatedSeriesLabels,
      series,
      groups,
      groupPadding,
      groupOuterPadding,
      seriesPadding,
      seriesOuterPadding,
      maxBarWidth,
    ],
  );

  const plotWidth = Math.max(0, chartWidth - plotLeft - marginRight);

  const plotTop = marginTop + VALUE_LABEL_AREA_HEIGHT;
  const resolvedMarginBottom = usesRotatedSeriesLabels
    ? getRotatedSeriesLabelsBottomMargin(series)
    : marginBottom;
  const plotBottom = Math.max(plotTop, chartHeight - resolvedMarginBottom - axisHeight);
  const plotHeight = Math.max(0, plotBottom - plotTop);
  const canRenderChart = plotWidth > 0 && plotHeight > 0 && groups.length > 0 && series.length > 0;

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, safeYMax],
        range: [plotBottom, plotTop],
        round: true,
      }),
    [plotBottom, plotTop, safeYMax],
  );

  const resolvedGroupOuterPadding = groupOuterPadding ?? (groups.length === 1 ? 0.42 : 0.22);

  const groupScale = useMemo(
    () =>
      scaleBand<string>({
        domain: groups.map((group) => group.id),
        range: [plotLeft, plotLeft + plotWidth],
        paddingInner: groupPadding,
        paddingOuter: resolvedGroupOuterPadding,
      }),
    [groupPadding, groups, plotLeft, plotWidth, resolvedGroupOuterPadding],
  );

  const seriesScale = useMemo(
    () =>
      scaleBand<string>({
        domain: series.map((item) => item.id),
        range: [0, groupScale.bandwidth()],
        paddingInner: seriesPadding,
        paddingOuter: seriesOuterPadding,
      }),
    [groupScale, series, seriesPadding, seriesOuterPadding],
  );

  const barWidth =
    maxBarWidth === null ? seriesScale.bandwidth() : Math.min(seriesScale.bandwidth(), maxBarWidth);
  const barOffset = Math.max(0, (seriesScale.bandwidth() - barWidth) / 2);
  const axisLabelFill = xAxisLabelColor ?? resolvedXAxisLabelColor;
  const tickLabelFill = yAxisLabelColor ?? resolvedYAxisLabelColor;
  const rowStroke = gridColor ?? resolvedGridColor;

  const handleBarMouseMove = (
    event: MouseEvent<SVGRectElement>,
    barEvent: BarChartBarClickEvent,
  ) => {
    if (!barInteractive) {
      return;
    }

    const point = localPoint(event);

    if (!point) {
      return;
    }

    showTooltip({
      tooltipData: barEvent,
      tooltipLeft: point.x,
      tooltipTop: point.y,
    });
  };

  const renderBarTooltip = (barEvent: BarChartBarClickEvent) => {
    if (formatBarTooltip) {
      return formatBarTooltip(barEvent);
    }

    return (
      <ChartSegmentTooltipContent
        label={barEvent.series.label}
        value={valueLabelFormatter(barEvent.value)}
      />
    );
  };

  return (
    <div
      style={{
        position: 'relative',
        width: fluidWidth ? '100%' : chartWidth,
        maxWidth: '100%',
        height: chartHeight,
      }}
    >
      <svg width={chartWidth} height={chartHeight}>
        {canRenderChart ? (
          <Group>
            {tickValues.map((tickValue) => {
              const y = yScale(tickValue);

              return (
                <Group key={`tick-${tickValue}`}>
                  <line
                    x1={plotLeft}
                    x2={plotLeft + plotWidth}
                    y1={y}
                    y2={y}
                    stroke={rowStroke}
                    strokeWidth={0.5}
                    strokeDasharray="2 2"
                  />
                  <text
                    x={Math.max(0, contentLeft - Y_AXIS_LABEL_GAP)}
                    y={y}
                    fill={tickLabelFill}
                    fontFamily="Inter, arial, sans-serif"
                    fontSize={AXIS_FONT_SIZE}
                    fontWeight={400}
                    textAnchor="end"
                    dominantBaseline="middle"
                  >
                    {formatBarChartValue(tickValue)}
                  </text>
                </Group>
              );
            })}

            {groups.map((group) => {
              const groupX = groupScale(group.id);

              if (groupX === undefined) {
                return null;
              }

              return (
                <Group key={group.id}>
                  {series.map((seriesItem) => {
                    const seriesX = seriesScale(seriesItem.id);

                    if (seriesX === undefined) {
                      return null;
                    }

                    const rawValue = toSafeBarValue(group.values[seriesItem.id]);
                    const isZero = rawValue === 0;
                    const barX = groupX + seriesX + barOffset;
                    const baselineHeight = showZeroBaseline && isZero ? 1 : 0;
                    const scaledBarTop = yScale(rawValue);
                    const barTop = baselineHeight > 0 ? plotBottom - baselineHeight : scaledBarTop;
                    const barHeight =
                      baselineHeight > 0 ? baselineHeight : Math.max(0, plotBottom - scaledBarTop);
                    const valueLabel = valueLabelFormatter(rawValue);
                    const valueLabelWidth = getValueLabelWidth(valueLabel);
                    const unclampedValueLabelX = barX + barWidth / 2 - valueLabelWidth / 2;
                    const valueLabelX = clamp(
                      unclampedValueLabelX,
                      plotLeft,
                      plotLeft + plotWidth - valueLabelWidth,
                    );
                    const valueLabelY = Math.max(
                      marginTop,
                      barTop - VALUE_LABEL_HEIGHT - VALUE_LABEL_GAP,
                    );

                    const shouldRenderValueLabel = !isZero || showZeroBaseline;
                    const barEvent: BarChartBarClickEvent = {
                      group,
                      series: seriesItem,
                      value: rawValue,
                    };

                    return (
                      <Group key={`${group.id}-${seriesItem.id}`}>
                        <Bar
                          x={barX}
                          y={barTop}
                          width={barWidth}
                          height={barHeight}
                          fill={seriesItem.color}
                          rx={barBorderRadius}
                          cursor={barInteractive ? 'pointer' : undefined}
                          onMouseMove={(event) => handleBarMouseMove(event, barEvent)}
                          onMouseLeave={hideTooltip}
                          onClick={() => onBarClick?.(barEvent)}
                        />
                        {shouldRenderValueLabel && (
                          <Group pointerEvents="none">
                            <rect
                              x={valueLabelX}
                              y={valueLabelY}
                              width={valueLabelWidth}
                              height={VALUE_LABEL_HEIGHT}
                              rx={8}
                              fill={seriesItem.valueLabelBackground ?? seriesItem.color}
                            />
                            <text
                              x={valueLabelX + valueLabelWidth / 2}
                              y={valueLabelY + VALUE_LABEL_HEIGHT / 2}
                              fill={seriesItem.valueLabelColor ?? defaultValueLabelColor}
                              fontFamily="Inter, arial, sans-serif"
                              fontSize={VALUE_LABEL_FONT_SIZE}
                              fontWeight={VALUE_LABEL_FONT_WEIGHT}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {valueLabel}
                            </text>
                          </Group>
                        )}
                        {xAxisMode === 'series' ? (
                          <BarChartAxisLabel
                            x={barX + barWidth / 2}
                            y={plotBottom + ROTATED_SERIES_LABEL_ANCHOR_OFFSET}
                            label={seriesItem.label}
                            fill={axisLabelFill}
                            fontSize={AXIS_FONT_SIZE}
                            fontWeight={AXIS_FONT_WEIGHT}
                            rotate={rotateSeriesLabels}
                          />
                        ) : null}
                      </Group>
                    );
                  })}
                  {xAxisMode === 'group' ? (
                    <text
                      x={groupX + groupScale.bandwidth() / 2}
                      y={plotBottom + 20}
                      fill={axisLabelFill}
                      fontFamily="Inter, arial, sans-serif"
                      fontSize={AXIS_FONT_SIZE}
                      fontWeight={AXIS_FONT_WEIGHT}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {truncateBarChartLabel(group.label, Math.max(groupScale.bandwidth(), 0))}
                    </text>
                  ) : null}
                </Group>
              );
            })}
          </Group>
        ) : null}
      </svg>
      {showBarTooltip && tooltipOpen && tooltipData ? (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={resolvedTooltipStyles}
          {...CHART_SEGMENT_TOOLTIP_OFFSET}
        >
          {renderBarTooltip(tooltipData)}
        </TooltipWithBounds>
      ) : null}
    </div>
  );
}

function getRotatedSeriesLabelLeftOverflow(label: string) {
  const lines = getBarChartAxisLabelLines(label, ROTATED_WRAP_LABEL_LENGTH_THRESHOLD);
  const maxLineWidth = Math.max(
    ...lines.map((line) => getStringWidth(line, null, AXIS_FONT_SIZE, AXIS_FONT_WEIGHT)),
  );

  return Math.ceil(maxLineWidth * Math.SQRT1_2) + ROTATED_SERIES_LABEL_LEFT_PADDING;
}

function resolveContentLeft(marginLeft: number, tickValues: number[]) {
  const yAxisLabelWidth =
    tickValues.length === 0
      ? 0
      : Math.max(
          ...tickValues.map((tick) =>
            getStringWidth(formatBarChartValue(tick), null, AXIS_FONT_SIZE, 400),
          ),
        );

  return marginLeft + Math.ceil(yAxisLabelWidth) + Y_AXIS_LABEL_GAP;
}

function resolveBarsPlotLeft({
  contentLeft,
  marginLeft,
  marginRight,
  chartWidth,
  usesRotatedSeriesLabels,
  series,
  groups,
  groupPadding,
  groupOuterPadding,
  seriesPadding,
  seriesOuterPadding,
  maxBarWidth,
}: {
  contentLeft: number;
  marginLeft: number;
  marginRight: number;
  chartWidth: number;
  usesRotatedSeriesLabels: boolean;
  series: BarChartSeries[];
  groups: BarChartGroup[];
  groupPadding: number;
  groupOuterPadding?: number;
  seriesPadding: number;
  seriesOuterPadding: number;
  maxBarWidth: number | null;
}) {
  let plotLeft = contentLeft;

  if (!usesRotatedSeriesLabels || series.length === 0 || groups.length === 0) {
    return plotLeft;
  }

  const firstGroup = groups[0];
  const firstSeries = series[0];

  if (!firstGroup || !firstSeries) {
    return plotLeft;
  }

  const resolvedGroupOuterPadding = groupOuterPadding ?? (groups.length === 1 ? 0.42 : 0.22);

  const resolveForBarsPlotLeft = (candidatePlotLeft: number) => {
    const plotWidth = Math.max(0, chartWidth - candidatePlotLeft - marginRight);

    if (plotWidth <= 0) {
      return candidatePlotLeft;
    }

    const groupScale = scaleBand<string>({
      domain: groups.map((group) => group.id),
      range: [candidatePlotLeft, candidatePlotLeft + plotWidth],
      paddingInner: groupPadding,
      paddingOuter: resolvedGroupOuterPadding,
    });
    const seriesScale = scaleBand<string>({
      domain: series.map((item) => item.id),
      range: [0, groupScale.bandwidth()],
      paddingInner: seriesPadding,
      paddingOuter: seriesOuterPadding,
    });
    const barWidth =
      maxBarWidth === null
        ? seriesScale.bandwidth()
        : Math.min(seriesScale.bandwidth(), maxBarWidth);
    const groupX = groupScale(firstGroup.id);
    const seriesX = seriesScale(firstSeries.id);

    if (groupX === undefined || seriesX === undefined) {
      return candidatePlotLeft;
    }

    const firstLabelAnchorX = groupX + seriesX + barWidth / 2;
    const leftOverflow = getRotatedSeriesLabelLeftOverflow(firstSeries.label);

    // Rotated labels may extend left into the y-axis label zone, down to marginLeft.
    return Math.max(
      candidatePlotLeft,
      marginLeft + leftOverflow + (candidatePlotLeft - firstLabelAnchorX),
    );
  };

  plotLeft = resolveForBarsPlotLeft(plotLeft);
  plotLeft = resolveForBarsPlotLeft(plotLeft);

  return plotLeft;
}

function getRotatedSeriesLabelsBottomMargin(series: BarChartSeries[]) {
  const margin = series.reduce((maxMargin, seriesItem) => {
    const lines = getBarChartAxisLabelLines(seriesItem.label, ROTATED_WRAP_LABEL_LENGTH_THRESHOLD);
    const maxLineWidth = Math.max(
      ...lines.map((line) => getStringWidth(line, null, AXIS_FONT_SIZE, AXIS_FONT_WEIGHT)),
    );
    const lineHeight = AXIS_FONT_SIZE * 1.2;
    const textBlockHeight = lines.length === 1 ? AXIS_FONT_SIZE : lineHeight * 2;
    const extraLineHeight = Math.max(0, textBlockHeight - AXIS_FONT_SIZE);
    const projectionBelowAnchor = maxLineWidth * Math.SQRT1_2 + extraLineHeight * Math.SQRT1_2;

    return Math.max(
      maxMargin,
      ROTATED_SERIES_LABEL_ANCHOR_OFFSET +
        projectionBelowAnchor +
        ROTATED_SERIES_LABEL_BOTTOM_PADDING,
    );
  }, 0);

  return Math.max(margin, ROTATED_SERIES_LABEL_MIN_BOTTOM_MARGIN);
}

function getBarChartScaleMax(groups: BarChartGroup[], series: BarChartSeries[]) {
  const maxValue = groups.reduce((currentMax, group) => {
    return Math.max(
      currentMax,
      ...series.map((seriesItem) => toSafeBarValue(group.values[seriesItem.id])),
    );
  }, 0);

  return maxValue > 0 ? maxValue : 1;
}

function getBarChartTickValues(
  yTickValues: number[] | undefined,
  yMax: number,
  integerTicks = false,
) {
  if (yTickValues && yTickValues.length > 0) {
    const sanitizedTickValues = [
      ...new Set(yTickValues.map((value) => toSafeBarValue(value))),
    ].sort((left, right) => right - left);

    if (sanitizedTickValues.length > 0) {
      if (integerTicks) {
        const uniqueIntegers = [...new Set(sanitizedTickValues.map((v) => Math.floor(v)))].sort(
          (a, b) => b - a,
        );
        return uniqueIntegers.length > 0 ? uniqueIntegers : [0];
      }
      return sanitizedTickValues;
    }
  }

  const safeYMax = toSafePositiveBarMax(yMax) ?? 0;

  if (safeYMax <= 0) {
    return [0];
  }

  if (integerTicks) {
    const integerYMax = Math.max(1, Math.ceil(safeYMax));

    if (integerYMax <= 5) {
      return Array.from({ length: integerYMax + 1 }, (_, index) => integerYMax - index);
    }

    const step = Math.ceil(integerYMax / 5);
    const ticks: number[] = [];
    for (let value = integerYMax; value >= 0; value -= step) {
      ticks.push(value);
    }
    if (ticks[ticks.length - 1] !== 0) {
      ticks.push(0);
    }
    return [...new Set(ticks)];
  }

  if (Number.isInteger(safeYMax) && safeYMax <= 5) {
    return Array.from({ length: safeYMax + 1 }, (_, index) => safeYMax - index);
  }

  return Array.from({ length: 6 }, (_, index) => {
    const value = safeYMax - (safeYMax / 5) * index;
    return Number.isInteger(value) ? value : Number(value.toFixed(1));
  });
}

function getValueLabelWidth(valueLabel: string) {
  return (
    getStringWidth(valueLabel, null, VALUE_LABEL_FONT_SIZE, VALUE_LABEL_FONT_WEIGHT) +
    VALUE_LABEL_PADDING_X * 2
  );
}

function truncateBarChartLabel(label: string, maxWidth: number) {
  if (maxWidth <= 0) {
    return label;
  }

  if (getStringWidth(label, null, AXIS_FONT_SIZE, AXIS_FONT_WEIGHT) <= maxWidth) {
    return label;
  }

  const suffix = '...';
  let truncated = label;

  while (truncated.length > 0) {
    const nextValue = `${truncated}${suffix}`;

    if (getStringWidth(nextValue, null, AXIS_FONT_SIZE, AXIS_FONT_WEIGHT) <= maxWidth) {
      return nextValue;
    }

    truncated = truncated.slice(0, -1);
  }

  return label;
}

function formatBarChartValue(value: number) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));
}

function clamp(value: number, minValue: number, maxValue: number) {
  return Math.min(Math.max(value, minValue), Math.max(minValue, maxValue));
}

function toSafeBarValue(value: number | undefined) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 0;
  }

  return numericValue;
}

function toSafePositiveBarMax(value: number | undefined) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return undefined;
  }

  return numericValue;
}
